import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { visitoriaService } from '@/services/vistoria'
import { fileService } from '@/services/file'
import type { VisitoriaData } from '@/types'
import { 
  FileText, 
  ArrowLeft, 
  Edit2, 
  Download, 
  Calendar, 
  FileCheck,
  User,
  Clock,
  Copy
} from 'lucide-react'

export function VisitoriaViewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const [vistoria, setVistoria] = useState<VisitoriaData | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  const loadVistoria = async () => {
    if (!id) {
      toast.error('ID da vistoria não fornecido')
      navigate('/vistorias')
      return
    }

    try {
      setLoading(true)
      const response = await visitoriaService.getById(parseInt(id), true) // with file
      
      if (response.success && response.data) {
        setVistoria(response.data)
      } else {
        toast.error('Vistoria não encontrada')
        navigate('/vistorias')
      }
    } catch (error) {
      console.error('Erro ao carregar vistoria:', error)
      toast.error('Erro ao carregar vistoria')
      navigate('/vistorias')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyId = () => {
    if (vistoria?.id_vistoria) {
      navigator.clipboard.writeText(vistoria.id_vistoria.toString())
      toast.success('ID copiado para a área de transferência')
    }
  }

  const handleDownload = async () => {
    if (!vistoria?.file?.id) {
      toast.error('Nenhum arquivo disponível para download')
      return
    }

    try {
      setDownloading(true)
      const fileName = vistoria.file.client_name || `vistoria_${vistoria.id_vistoria}_arquivo`
      await fileService.downloadFileById(vistoria.file.id, fileName)
      toast.success('Download concluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao fazer download:', error)
      toast.error(error.message || 'Erro ao fazer download do arquivo')
    } finally {
      setDownloading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  useEffect(() => {
    loadVistoria()
  }, [id])

  if (loading) {
    return (
      <div className="w-full">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <p className="mt-2 text-gray-600">Carregando vistoria...</p>
        </div>
      </div>
    )
  }

  if (!vistoria) {
    return (
      <div className="w-full">
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Vistoria não encontrada
          </h3>
          <p className="text-gray-600 mb-4">
            A vistoria solicitada não existe ou foi removida
          </p>
          <Button onClick={() => navigate('/vistorias')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para lista
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Cabeçalho da página */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/vistorias')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          <div className="flex space-x-2">
            {vistoria.file && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                loading={downloading}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/vistorias/editar/${vistoria.id_vistoria}`)}
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <FileText className="w-8 h-8 text-purple-600" />
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">
              {vistoria.referencia}
            </h1>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {formatDate(vistoria.data_cadastro)}
              </span>
              <button 
                onClick={handleCopyId}
                className="flex items-center hover:text-purple-600 transition-colors"
                title="Clique para copiar o ID"
              >
                <Copy className="w-4 h-4 mr-1" />
                ID: #{vistoria.id_vistoria}
              </button>
              {vistoria.file && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700 border border-green-200">
                  <FileCheck className="w-3 h-3 mr-1" />
                  Com Arquivo
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Estatísticas Rápidas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Status</p>
                <p className="text-lg font-semibold text-gray-900">Ativa</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Arquivo</p>
                <p className="text-lg font-semibold text-gray-900">
                  {vistoria.file ? 'Anexado' : 'Sem arquivo'}
                </p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Criada há</p>
                <p className="text-lg font-semibold text-gray-900">
                  {Math.ceil((Date.now() - new Date(vistoria.data_cadastro).getTime()) / (1000 * 60 * 60 * 24))} dias
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Informações Principais */}
        <Card title="Detalhes da Vistoria">
          <div className="space-y-6">
            {/* Referência */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Referência
              </label>
              <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                {vistoria.referencia}
              </p>
            </div>

            {/* Coordenadas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coordenadas
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Latitude
                  </label>
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {vistoria.latitude ? parseFloat(vistoria.latitude).toFixed(6) : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Longitude
                  </label>
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {vistoria.longitude ? parseFloat(vistoria.longitude).toFixed(6) : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Precisão
                  </label>
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {vistoria.accuracy ? `${parseFloat(vistoria.accuracy).toFixed(0)}m` : 'Não informada'}
                  </p>
                </div>
              </div>
            </div>

            {/* Informações de Sistema */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Data de Cadastro
                </label>
                <p className="text-gray-900">
                  {formatDate(vistoria.data_cadastro)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Última Atualização
                </label>
                <p className="text-gray-900">
                  {formatDate(vistoria.updated_at)}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Arquivo Anexo */}
        {vistoria.file && (
          <Card title="Arquivo Anexo">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {vistoria.file.client_name || 'Arquivo da vistoria'}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>Tipo: {vistoria.file.type || 'N/A'}</span>
                      {vistoria.file.subtype && (
                        <span>Subtipo: {vistoria.file.subtype}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  loading={downloading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Ações */}
        <Card>
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Ações</h3>
              <p className="text-sm text-gray-600">
                Gerenciar esta vistoria
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate(`/vistorias/editar/${vistoria.id_vistoria}`)}
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Editar Vistoria
              </Button>
              
              {vistoria.file && (
                <Button
                  onClick={handleDownload}
                  loading={downloading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Arquivo
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
